/**
 * InspectionReportPreviewDialog - Visualizador interno do PDF completo da vistoria
 *
 * Usa uma URL assinada temporária em modo de visualização e preserva o download
 * protegido como uma ação separada.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-27 v1.0.0 - Versão inicial baseada no preview de Propostas.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, ExternalLink, FileText, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  downloadChecklistInstanceReport,
  getChecklistInstanceReportUrl,
} from '@/hooks/useChecklistInstanceReport';

interface InspectionReportPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  instanceId: string;
  title?: string;
}

export function InspectionReportPreviewDialog({
  open,
  onClose,
  instanceId,
  title,
}: InspectionReportPreviewDialogProps) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestedInstanceRef = useRef<string | null>(null);

  /** Obtém um link temporário que permite renderizar o PDF no navegador. */
  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    const url = await getChecklistInstanceReportUrl(instanceId, 'preview');
    if (url) {
      setSignedUrl(url);
    } else {
      setSignedUrl(null);
      setError('Não foi possível carregar o PDF da vistoria.');
    }
    setLoading(false);
  }, [instanceId]);

  useEffect(() => {
    if (!open) {
      requestedInstanceRef.current = null;
      setSignedUrl(null);
      setError(null);
      return;
    }
    if (requestedInstanceRef.current === instanceId) return;
    requestedInstanceRef.current = instanceId;
    void loadPreview();
  }, [instanceId, loadPreview, open]);

  /** Mantém o download separado do link usado pelo iframe. */
  const handleDownload = async () => {
    setDownloading(true);
    await downloadChecklistInstanceReport(instanceId);
    setDownloading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="h-[100dvh] w-screen max-w-none rounded-none border-0 p-0 gap-0 flex flex-col md:h-[92dvh] md:w-[95vw] md:max-w-[95vw] md:rounded-lg md:border">
        <DialogHeader className="border-b px-4 py-3">
          <DialogTitle className="flex min-w-0 items-center gap-2 pr-8 text-base">
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{title || 'PDF da vistoria'}</span>
            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              {signedUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => window.open(signedUrl, '_blank', 'noopener,noreferrer')}
                >
                  <ExternalLink className="mr-1 h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Nova aba</span>
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={downloading}
                onClick={handleDownload}
              >
                {downloading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />}
                <span className="hidden sm:inline">Baixar</span>
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 bg-muted/30">
          {loading ? (
            <div className="flex h-full flex-col items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Carregando o PDF...</p>
            </div>
          ) : signedUrl ? (
            <iframe
              key={signedUrl}
              src={signedUrl}
              title="Pré-visualização do PDF da vistoria"
              className="h-full w-full border-0 bg-background"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <FileText className="h-8 w-8 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">{error || 'PDF da vistoria indisponível.'}</p>
              <Button type="button" size="sm" variant="outline" onClick={loadPreview}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                Tentar novamente
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}